import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface Announcement {
  id: string;
  title: string;
  content: string | null;
  priority: 'urgent' | 'normal' | 'celebration';
  created_by: string | null;
  created_at: string;
  updated_at: string;
  expires_at: string | null;
  is_active: boolean;
  created_by_user?: {
    display_name: string | null;
    first_name: string | null;
  } | null;
}

export interface AnnouncementInsert {
  title: string;
  content?: string | null;
  priority?: 'urgent' | 'normal' | 'celebration';
  expires_at?: string | null;
  created_by?: string;
}

export interface AnnouncementUpdate {
  id: string;
  title?: string;
  content?: string | null;
  priority?: 'urgent' | 'normal' | 'celebration';
  expires_at?: string | null;
  is_active?: boolean;
}

/**
 * Hook para buscar avisos ativos (para exibição na Home)
 * - Filtra por is_active = true
 * - Filtra expirados
 * - Ordena: urgentes primeiro, depois por data
 * - Cache de 2 minutos
 */
export function useAnnouncements(limit = 5) {
  return useQuery({
    queryKey: ['announcements', 'active', limit],
    queryFn: async () => {
      const now = new Date().toISOString();
      
      const { data, error } = await supabase
        .from('announcements')
        .select(`
          id, title, content, priority, created_at, updated_at, expires_at, is_active, created_by,
          created_by_user:app_users!announcements_created_by_fkey(display_name, first_name)
        `)
        .eq('is_active', true)
        .or(`expires_at.is.null,expires_at.gt.${now}`)
        .order('created_at', { ascending: false })
        .limit(limit);
      
      if (error) throw error;
      
      // Ordenar: urgent primeiro, depois celebration, depois normal
      const priorityOrder = { urgent: 0, celebration: 1, normal: 2 };
      const sorted = (data || []).sort((a, b) => {
        const priorityA = priorityOrder[a.priority as keyof typeof priorityOrder] ?? 2;
        const priorityB = priorityOrder[b.priority as keyof typeof priorityOrder] ?? 2;
        if (priorityA !== priorityB) return priorityA - priorityB;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
      
      return sorted as Announcement[];
    },
    staleTime: 2 * 60 * 1000, // 2 minutos
    gcTime: 5 * 60 * 1000,    // 5 minutos
  });
}

/**
 * Hook para buscar todos os avisos (para admin)
 */
export function useAllAnnouncements() {
  return useQuery({
    queryKey: ['announcements', 'all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('announcements')
        .select(`
          id, title, content, priority, created_at, updated_at, expires_at, is_active, created_by,
          created_by_user:app_users!announcements_created_by_fkey(display_name, first_name)
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as Announcement[];
    },
    staleTime: 1 * 60 * 1000, // 1 minuto
  });
}

/**
 * Hook para criar aviso
 */
export function useCreateAnnouncement() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (announcement: AnnouncementInsert) => {
      const { data, error } = await supabase
        .from('announcements')
        .insert(announcement)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
    },
  });
}

/**
 * Hook para atualizar aviso
 */
export function useUpdateAnnouncement() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: AnnouncementUpdate) => {
      const { data, error } = await supabase
        .from('announcements')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
    },
  });
}

/**
 * Hook para deletar aviso
 */
export function useDeleteAnnouncement() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('announcements')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
    },
  });
}
